import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { RefreshCw, CheckCircle, AlertCircle, Clock, ChevronDown, ChevronUp, XCircle } from "lucide-react";
import toast from "react-hot-toast";
import api, { syncSquareRefunds } from "../../services/api";

// ─── Types ────────────────────────────────────────────────────────────────────

type RefundStatus = "refund_initiated" | "processing" | "completed" | "failed";
type StatusFilter = "all" | RefundStatus;

interface RefundEntry {
  refundId: string;
  amount: number;
  reason: string;
  reasonNote?: string;
  status: RefundStatus;
  refundTo?: string;
  externalRefundId?: string;
  initiatedAt: string;
  completedAt?: string;
}

interface PaymentLeg {
  legId: string;
  method: string;
  amount: number;
  amountRefunded: number;
  netAmount: number;
  status: string;
  reference?: string;
  refunds: RefundEntry[];
}

interface RefundRow {
  _id: string;
  transactionId: string;
  orderId: string;
  orderNumber: string | null;
  customerPhone: string | null;
  orderAmount: number;
  totalPaid: number;
  totalRefunded: number;
  netAmount: number;
  currency: string;
  status: string;
  legs: PaymentLeg[];
  createdAt: string;
  updatedAt: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const METHOD_LABELS: Record<string, string> = {
  stripe: "Square",
  square: "Square",
  cash: "Cash",
  wallet: "Wallet",
  bonus_wallet: "Bonus Wallet",
  voucher: "Voucher",
  gift_card: "Gift Card",
  loyalty_points: "Loyalty Points",
};

const METHOD_COLORS: Record<string, string> = {
  stripe: "bg-purple-100 text-purple-700",
  square: "bg-slate-100 text-slate-700",
  cash: "bg-green-100 text-green-700",
  wallet: "bg-blue-100 text-blue-700",
  bonus_wallet: "bg-orange-100 text-orange-700",
  voucher: "bg-pink-100 text-pink-700",
  gift_card: "bg-teal-100 text-teal-700",
  loyalty_points: "bg-yellow-100 text-yellow-700",
};

const STATUS_LABELS: Record<RefundStatus, string> = {
  refund_initiated: "Initiated",
  processing: "Processing",
  completed: "Completed",
  failed: "Failed",
};

const STATUS_COLORS: Record<RefundStatus, string> = {
  refund_initiated: "bg-amber-100 text-amber-700",
  processing: "bg-blue-100 text-blue-700",
  completed: "bg-green-100 text-green-700",
  failed: "bg-red-100 text-red-700",
};

const FILTERS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "refund_initiated", label: "Initiated" },
  { value: "processing", label: "Processing" },
  { value: "completed", label: "Completed" },
  { value: "failed", label: "Failed" },
];

const PAGE_SIZE = 20;

const EMPTY_COUNTS: Record<StatusFilter, number> = {
  all: 0,
  refund_initiated: 0,
  processing: 0,
  completed: 0,
  failed: 0,
};

interface RefundsResponse {
  success: boolean;
  data: RefundRow[];
  hasMore: boolean;
  counts: Record<StatusFilter, number>;
}

// Processor-backed methods are refunded via "Process All", never marked done by hand
const PROCESSOR_METHODS = ["stripe", "square"];

function fmt(amount: number, currency = "USD") {
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(amount);
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const getRefundItems = (row: RefundRow) =>
  row.legs.flatMap((leg) => leg.refunds.map((refund) => ({ leg, refund })));

// ─── Component ────────────────────────────────────────────────────────────────

const Refunds: React.FC = () => {
  const [rows, setRows] = useState<RefundRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [counts, setCounts] = useState<Record<StatusFilter, number>>(EMPTY_COUNTS);
  const [processing, setProcessing] = useState<Record<string, boolean>>({});
  const [completing, setCompleting] = useState<Record<string, boolean>>({});
  const [syncing, setSyncing] = useState<Record<string, boolean>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [filter, setFilter] = useState<StatusFilter>("all");

  const restaurantId = process.env.REACT_APP_RESTAURANT_ID;

  // Guards against stale responses when the filter changes mid-request
  const requestIdRef = useRef(0);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // Load one page; page 1 replaces the list, later pages append to it
  const fetchRefunds = useCallback(
    async (pageNum = 1) => {
      const requestId = ++requestIdRef.current;
      if (pageNum === 1) setLoading(true);
      else setLoadingMore(true);
      try {
        const res = await api.get<RefundsResponse>("/transactions/pending-refunds", {
          params: { restaurantId, status: filter, page: pageNum, limit: PAGE_SIZE },
        });
        if (requestId !== requestIdRef.current || !res.data.success) return;
        setRows((prev) => {
          if (pageNum === 1) return res.data.data;
          const seen = new Set(prev.map((r) => r.transactionId));
          return [...prev, ...res.data.data.filter((r) => !seen.has(r.transactionId))];
        });
        setPage(pageNum);
        setHasMore(res.data.hasMore);
        setCounts({ ...EMPTY_COUNTS, ...res.data.counts });
      } catch {
        if (requestId === requestIdRef.current) toast.error("Failed to load refunds");
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [restaurantId, filter],
  );

  useEffect(() => {
    fetchRefunds(1);
  }, [fetchRefunds]);

  // Load the next page when the sentinel at the bottom scrolls into view
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore || loading || loadingMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) fetchRefunds(page + 1);
      },
      { rootMargin: "200px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, page, fetchRefunds]);

  const visibleRows = useMemo(
    () =>
      rows
        .map((row) => ({
          row,
          items: getRefundItems(row).filter(
            ({ refund }) => filter === "all" || refund.status === filter,
          ),
        }))
        .filter(({ items }) => items.length > 0),
    [rows, filter],
  );

  // Process all refund_initiated refunds for a transaction (calls Stripe/Square API for card legs)
  const handleProcessAll = async (row: RefundRow) => {
    setProcessing((p) => ({ ...p, [row.transactionId]: true }));
    try {
      await api.post(`/transactions/order/${row.orderId}/process-refunds`);
      toast.success(`Refunds processed for Order #${row.orderNumber ?? row.orderId}`);
      fetchRefunds(1);
    } catch (err: any) {
      toast.error(err?.response?.data?.error ?? "Failed to process refunds");
    } finally {
      setProcessing((p) => ({ ...p, [row.transactionId]: false }));
    }
  };

  // Ask Square for the current status of "processing" refunds (fallback when the webhook is missed)
  const handleSyncSquare = async (row: RefundRow) => {
    setSyncing((p) => ({ ...p, [row.transactionId]: true }));
    try {
      const res = await syncSquareRefunds(row.orderId);
      const results: { newStatus: string; error?: string }[] = res.data?.results ?? [];
      const completed = results.filter((r) => r.newStatus === "completed").length;
      const failed = results.filter((r) => r.newStatus === "failed").length;
      const errors = results.filter((r) => r.newStatus === "processing" && r.error);
      if (errors.length) toast.error(`Square check failed: ${errors[0].error}`);
      else if (completed || failed)
        toast.success(`${completed} completed, ${failed} failed`);
      else toast("Still processing at Square");
      fetchRefunds(1);
    } catch (err: any) {
      toast.error(err?.response?.data?.error ?? "Failed to check Square status");
    } finally {
      setSyncing((p) => ({ ...p, [row.transactionId]: false }));
    }
  };

  // Manually complete a single refund entry (for non-card methods or manual override)
  const handleCompleteOne = async (
    row: RefundRow,
    legId: string,
    refundId: string,
  ) => {
    const key = `${row.transactionId}-${refundId}`;
    setCompleting((c) => ({ ...c, [key]: true }));
    try {
      await api.patch(
        `/transactions/order/${row.orderId}/legs/${legId}/refunds/${refundId}/complete`,
      );
      toast.success("Refund marked as completed");
      fetchRefunds(1);
    } catch (err: any) {
      toast.error(err?.response?.data?.error ?? "Failed to complete refund");
    } finally {
      setCompleting((c) => ({ ...c, [key]: false }));
    }
  };

  const toggleExpand = (id: string) =>
    setExpanded((e) => ({ ...e, [id]: !e[id] }));

  return (
    <div className="max-w-6xl mx-auto px-3 py-4 sm:px-5 sm:py-6 lg:px-6">
      {/* Header */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Refunds</h1>
          <p className="text-sm text-gray-500 mt-1">
            All refunds across orders — pending, processing, completed and failed
          </p>
        </div>
        <button
          onClick={() => fetchRefunds(1)}
          disabled={loading}
          className="flex w-full items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium transition-colors hover:bg-gray-50 disabled:opacity-50 sm:w-auto"
        >
          <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {/* Status filter */}
      <div className="mb-6 flex gap-2 overflow-x-auto pb-1">
        {FILTERS.map(({ value, label }) => (
          <button
            key={value}
            onClick={() => setFilter(value)}
            className={`flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
              filter === value
                ? "border-blue-600 bg-blue-600 text-white"
                : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
            }`}
          >
            {label}
            <span
              className={`rounded-full px-1.5 text-xs ${
                filter === value ? "bg-white/20" : "bg-gray-100 text-gray-500"
              }`}
            >
              {counts[value]}
            </span>
          </button>
        ))}
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-20">
          <RefreshCw size={28} className="animate-spin text-blue-500" />
        </div>
      )}

      {/* Empty */}
      {!loading && visibleRows.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <CheckCircle size={48} className="mb-3 text-green-400" />
          <p className="text-lg font-medium">No refunds</p>
          <p className="text-sm">
            {filter === "all"
              ? "No refunds have been recorded yet."
              : `No ${STATUS_LABELS[filter].toLowerCase()} refunds right now.`}
          </p>
        </div>
      )}

      {/* Rows */}
      {!loading &&
        visibleRows.map(({ row, items }) => {
          const allItems = getRefundItems(row);
          const initiatedItems = allItems.filter(
            ({ refund }) => refund.status === "refund_initiated",
          );
          const totalPending = allItems
            .filter(({ refund }) =>
              refund.status === "refund_initiated" || refund.status === "processing",
            )
            .reduce((s, i) => s + i.refund.amount, 0);
          const rowStatuses = Array.from(
            new Set(allItems.map(({ refund }) => refund.status)),
          ) as RefundStatus[];
          const hasProcessingSquare = allItems.some(
            ({ leg, refund }) =>
              leg.method === "square" &&
              refund.status === "processing" &&
              refund.externalRefundId,
          );
          const isOpen = expanded[row.transactionId];
          const isProcessing = processing[row.transactionId];
          const isSyncing = syncing[row.transactionId];

          return (
            <div
              key={row.transactionId}
              className="bg-white rounded-xl border border-gray-200 shadow-sm mb-4 overflow-hidden"
            >
              {/* Card header */}
              <div className="flex flex-col gap-3 px-4 py-4 sm:px-5 lg:flex-row lg:items-start lg:justify-between">
                <div className="flex flex-col gap-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-bold text-gray-900 text-base">
                      Order #{row.orderNumber ?? "—"}
                    </span>
                    {rowStatuses.map((s) => (
                      <span
                        key={s}
                        className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[s]}`}
                      >
                        {STATUS_LABELS[s]}
                      </span>
                    ))}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
                    {row.customerPhone && <span>{row.customerPhone}</span>}
                    <span>{fmtDate(row.updatedAt)}</span>
                  </div>
                </div>

                {/* Amounts */}
                <div className="grid w-full grid-cols-3 gap-3 text-sm sm:gap-6 lg:w-auto lg:self-start">
                  <div className="text-center sm:text-right">
                    <p className="min-h-[2rem] text-xs leading-tight text-gray-400 m-0">
                      Order Total
                    </p>
                    <p className="font-semibold text-gray-800">{fmt(row.orderAmount, row.currency)}</p>
                  </div>
                  <div className="text-center sm:text-right">
                    <p className="min-h-[2rem] text-xs leading-tight text-gray-400 m-0">
                      Pending
                    </p>
                    <p className={`font-bold ${totalPending > 0 ? "text-red-600" : "text-gray-400"}`}>
                      {fmt(totalPending, row.currency)}
                    </p>
                  </div>
                  <div className="text-center sm:text-right">
                    <p className="min-h-[2rem] text-xs leading-tight text-gray-400 m-0">
                      Refunded
                    </p>
                    <p className="font-semibold text-green-700">{fmt(row.totalRefunded, row.currency)}</p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex w-full items-center gap-2 lg:w-auto lg:flex-shrink-0 lg:self-start">
                  {initiatedItems.length > 0 && (
                    <button
                      onClick={() => handleProcessAll(row)}
                      disabled={isProcessing}
                      className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-60 lg:flex-none"
                    >
                      {isProcessing ? (
                        <RefreshCw size={14} className="animate-spin" />
                      ) : (
                        <CheckCircle size={14} />
                      )}
                      {isProcessing ? "Processing…" : "Process All"}
                    </button>
                  )}
                  {hasProcessingSquare && (
                    <button
                      onClick={() => handleSyncSquare(row)}
                      disabled={isSyncing}
                      className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-blue-200 bg-white px-4 py-2 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-50 disabled:opacity-60 lg:flex-none"
                    >
                      <RefreshCw size={14} className={isSyncing ? "animate-spin" : ""} />
                      {isSyncing ? "Checking…" : "Check Status"}
                    </button>
                  )}
                  <button
                    onClick={() => toggleExpand(row.transactionId)}
                    className="ml-auto p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                    title="Show breakdown"
                  >
                    {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                </div>
              </div>

              {/* Expandable breakdown */}
              {isOpen && (
                <div className="border-t border-gray-100 bg-gray-50 px-4 py-4 sm:px-5">
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
                    Refund Breakdown
                  </p>
                  <div className="space-y-2">
                    {items.map(({ leg, refund }) => {
                      const key = `${row.transactionId}-${refund.refundId}`;
                      const isCompleting = completing[key];
                      const isInitiated = refund.status === "refund_initiated";
                      const isProcessorLeg = PROCESSOR_METHODS.includes(leg.method);
                      return (
                        <div
                          key={refund.refundId}
                          className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 md:flex-row md:items-center md:justify-between"
                        >
                          <div className="flex items-start gap-3 sm:items-center">
                            <span
                              className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${METHOD_COLORS[leg.method] ?? "bg-gray-100 text-gray-600"}`}
                            >
                              {METHOD_LABELS[leg.method] ?? leg.method}
                            </span>
                            <div>
                              <p className="text-sm font-semibold text-gray-800 m-0">
                                {fmt(refund.amount, row.currency)}
                              </p>
                              <p className="text-xs text-gray-400">
                                {refund.reason.replace(/_/g, " ")}
                                {refund.reasonNote ? ` — ${refund.reasonNote}` : ""}
                              </p>
                              {refund.externalRefundId && (
                                <p className="text-xs text-gray-400 font-mono break-all">
                                  Ref: {refund.externalRefundId}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col items-start gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
                            <span
                              className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[refund.status]}`}
                            >
                              {STATUS_LABELS[refund.status]}
                            </span>
                            {refund.status === "completed" && refund.completedAt ? (
                              <div className="flex items-center gap-1 rounded-full bg-green-50 px-2 py-1 text-xs text-green-700">
                                <CheckCircle size={11} />
                                Completed {fmtDate(refund.completedAt)}
                              </div>
                            ) : refund.status === "failed" ? (
                              <div className="flex items-center gap-1 rounded-full bg-red-50 px-2 py-1 text-xs text-red-600">
                                <XCircle size={11} />
                                Initiated {fmtDate(refund.initiatedAt)}
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 rounded-full bg-amber-50 px-2 py-1 text-xs text-amber-600">
                                <Clock size={11} />
                                Initiated {fmtDate(refund.initiatedAt)}
                              </div>
                            )}
                            {/* Card refund awaiting processing → goes through the processor API */}
                            {isInitiated && isProcessorLeg && leg.reference && (
                              <span className="text-xs text-gray-400 italic">
                                Use "Process All" ↑
                              </span>
                            )}
                            {/* Card refund without reference (edge case) */}
                            {isInitiated && isProcessorLeg && !leg.reference && (
                              <span className="flex items-center gap-1 text-xs text-red-500">
                                <AlertCircle size={12} />
                                No payment ref
                              </span>
                            )}
                            {/* Cash / wallet / bonus_wallet → manual confirmation */}
                            {isInitiated && !isProcessorLeg && (
                              <button
                                onClick={() =>
                                  handleCompleteOne(row, leg.legId, refund.refundId)
                                }
                                disabled={isCompleting}
                                className="flex w-full items-center justify-center gap-1 rounded-lg bg-green-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-green-700 disabled:opacity-60 sm:w-auto"
                              >
                                {isCompleting ? (
                                  <RefreshCw size={11} className="animate-spin" />
                                ) : (
                                  <CheckCircle size={11} />
                                )}
                                {leg.method === "cash" ? "Confirm Cash Returned" : "Mark Done"}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Full leg summary */}
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mt-5 mb-2">
                    All Payment Legs
                  </p>
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                    {row.legs.map((leg) => (
                      <div
                        key={leg.legId}
                        className="flex gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 flex-row items-center justify-between"
                      >
                        <div className="flex items-center gap-2">
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full font-medium ${METHOD_COLORS[leg.method] ?? "bg-gray-100 text-gray-600"}`}
                          >
                            {METHOD_LABELS[leg.method] ?? leg.method}
                          </span>
                          <span className="text-sm font-medium text-gray-700">
                            {fmt(leg.amount, row.currency)}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 text-right">
                          <span>
                            Refunded:{" "}
                            <strong className="text-red-500">
                              {fmt(leg.amountRefunded, row.currency)}
                            </strong>
                          </span>
                          <br />
                          <span>
                            Net:{" "}
                            <strong className="text-gray-800">
                              {fmt(leg.netAmount, row.currency)}
                            </strong>
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}

      {/* Infinite-scroll sentinel */}
      {!loading && hasMore && <div ref={sentinelRef} className="h-1" />}
      {loadingMore && (
        <div className="flex justify-center py-6">
          <RefreshCw size={20} className="animate-spin text-blue-500" />
        </div>
      )}
      {!loading && !hasMore && visibleRows.length > 0 && (
        <p className="py-6 text-center text-xs text-gray-400">No more refunds</p>
      )}
    </div>
  );
};

export default Refunds;
